import React from "react"
import * as IconFactory from "app/components/common/IconFactory.jsx"

import { cn } from "@/lib/utils"

/** Any icon the app's IconFactory exports. */
export type ServiceIconName = keyof typeof IconFactory

/**
 * A square mark for any `IconFactory` icon at any pixel size.
 *
 * The escape hatch beside `<Networks>`, for marks that aren't a network and so
 * have no registry slug: the retweet badge an X account carries when it isn't
 * the main tweeter is `IconFactory`'s `retweet`, and it has to size and align
 * exactly like the network badge it replaces. Reach for `<Networks>` first — a
 * brand mark belongs in the registry, and this renders from the legacy
 * `dlvr-socials` font that `dlvr-networks` was built to replace.
 *
 * IconFactory's own size props are a fixed ladder (16 / 26 / 35 / 55px) that
 * doesn't line up with this flow's boxes, so `size` is a pixel edge and the
 * icon is stretched to fill it. The glyph rides along in `em` because the
 * wrapper's font-size *is* the box size — one number drives both.
 *
 * A responsive size needs `!`-flagged utilities (`tw:sm:size-[75px]!
 * tw:sm:text-[75px]!`) on `className`, since the base size is an inline style.
 *
 * The `!` on every override below is load-bearing rather than a specificity
 * shortcut: Tailwind emits into `@layer utilities` while dlvrit.min.css is
 * unlayered, and a normal unlayered declaration outranks any layered one
 * however specific. Important declarations invert that order, so this is the
 * only way a utility beats `.dlvr-icon`'s hard-coded 26px box and 20px glyph.
 */
export function ServiceMark({
  icon,
  label,
  size = 24,
  round = true,
  className,
}: {
  icon: ServiceIconName
  label: string
  /** Edge of the square mark, in px. */
  size?: number
  round?: boolean
  className?: string
}) {
  const Icon = IconFactory[icon] as React.ComponentType

  return (
    <span
      role="img"
      aria-label={label}
      style={{ width: size, height: size, fontSize: size }}
      className={cn(
        "tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center",
        "tw:[&_.dlvr-icon-wrapper]:size-full! tw:[&_.dlvr-icon]:size-full!",
        round
          ? "tw:[&_.dlvr-icon]:rounded-full!"
          : "tw:[&_.dlvr-icon]:rounded-[0.2em]!",
        // 0.72em reproduces IconFactory's own 20px-glyph-in-a-26px-box ratio.
        "tw:[&_.dlvr-icon_i]:text-[0.72em]! tw:[&_.dlvr-icon_i]:leading-none!",
        className
      )}
    >
      <Icon />
    </span>
  )
}
